import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import EventCalendar from '../components/EventCalendar';
import EventCard from '../components/EventCard';
import EventDescription from '../components/EventDescription';
import EventDetailsGrid from '../components/EventDetailsGrid';
import NewsletterPanel from '../components/NewsletterPanel';
import { Event, EventCatalogs } from '../types';
import { mockBackend } from '../services/mockBackend';
import { resolveBrowser, resolveDevice, resolveVisitorId } from '../utils/accessTracking';

const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<Event[]>([]);
  const [catalogs, setCatalogs] = useState<EventCatalogs>({ institutions: [], audiences: [] });
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(
    () => ({
      query: searchParams.get('q') || '',
      institution: searchParams.get('institution') || '',
      format: (searchParams.get('format') as 'presencial' | 'online' | 'hibrido' | 'all' | null) || 'all',
    }),
    [searchParams]
  );

  useEffect(() => {
    setLoading(true);
    mockBackend.events
      .listPublicEvents({
        query: filters.query || undefined,
        institution: filters.institution || undefined,
        format: filters.format || 'all',
      })
      .then((response) => {
        setEvents(response.items);
        setCatalogs(response.catalogs);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const trackRegistrationClick = (event: Event) => {
    if (!event.registrationLink) return;

    mockBackend.analytics
      .trackRegistrationClick({
        eventId: event.id,
        eventTitle: event.title,
        registrationLink: event.registrationLink,
        visitorId: resolveVisitorId(),
        device: resolveDevice(),
        browser: resolveBrowser(),
      })
      .catch(() => undefined);
  };

  return (
    <div className="home-page">
      <section className="hero-section py-5 position-relative overflow-hidden">
        <div className="hero-blur" />
        <div className="container py-4">
          <div className="row align-items-center g-4">
            <div className="col-lg-7">
              <span className="badge bg-brand text-white fw-semibold px-3 py-2 rounded-pill mb-3">
                Inovação & Tecnologia
              </span>
              <h1 className="display-5 fw-bold text-white mb-3">Calendário de Inovação</h1>
              <p className="lead text-white-50 mb-4">
                Aqui as oportunidades e encontros propiciam a união das iniciativas e esforços e
                fortalecem ainda mais o ecossistema de inovação.
              </p>
              <div className="d-flex flex-wrap gap-3">
                <a href="#calendario" className="btn btn-brand px-4 py-2 fw-semibold">
                  Ver calendário
                </a>
                <Link to="/acesso" className="btn btn-outline-light px-4 py-2 fw-semibold">
                  Ir para o portal
                </Link>
              </div>
            </div>

            <div className="col-lg-5">
              <div className="d-grid gap-3">
                <div className="glass-card p-4">
                  <p className="text-white-50 mb-1">Pipeline de inovação</p>
                  <h3 className="text-white mb-2">Eventos estratégicos</h3>
                  <p className="text-white-50 small mb-0">
                    Acompanhe hackathons, workshops, meetups e ações que conectam governo, startups e
                    comunidade.
                  </p>
                </div>
                <div className="d-flex gap-3">
                  <div className="glass-card flex-fill p-3 text-center">
                    <p className="text-white-50 mb-1 small">Experiência</p>
                    <h4 className="text-white mb-0">100% digital</h4>
                  </div>
                  <div className="glass-card flex-fill p-3 text-center">
                    <p className="text-white-50 mb-1 small">Integração</p>
                    <h4 className="text-white mb-0">Equipe + comunidade</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 section-contrast" id="calendario">
        <div className="container">
          <div className="row g-4 align-items-start">
            <div className="col-xl-7">
              <div className="card bg-onyx border-0 shadow-lg mb-4">
                <div className="card-body p-4">
                  <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-4">
                    <div>
                      <h4 className="text-white mb-1">Encontre eventos</h4>
                      <p className="text-white-50 mb-0">
                        Busque por título, instituição e formato e veja o resultado logo abaixo.
                      </p>
                    </div>
                    <span className="badge bg-brand text-white home-results-badge">
                      {loading ? 'Buscando...' : `${events.length} resultado${events.length === 1 ? '' : 's'}`}
                    </span>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label text-white" htmlFor="public-query">
                        Buscar eventos
                      </label>
                      <input
                        id="public-query"
                        className="form-control input-contrast"
                        placeholder="Título, descrição, instituição ou local"
                        value={filters.query}
                        onChange={(event) => updateFilter('q', event.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white" htmlFor="public-institution">
                        Instituição
                      </label>
                      <select
                        id="public-institution"
                        className="form-select input-contrast"
                        value={filters.institution}
                        onChange={(event) => updateFilter('institution', event.target.value)}
                      >
                        <option value="">Todas</option>
                        {catalogs.institutions.map((institution) => (
                          <option key={institution} value={institution}>
                            {institution}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label text-white" htmlFor="public-format">
                        Formato
                      </label>
                      <select
                        id="public-format"
                        className="form-select input-contrast"
                        value={filters.format}
                        onChange={(event) => updateFilter('format', event.target.value)}
                      >
                        <option value="all">Todos</option>
                        <option value="presencial">Presencial</option>
                        <option value="online">Online</option>
                        <option value="hibrido">Híbrido</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card bg-onyx border-0 shadow-lg">
                <div className="card-body p-4">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
                    <div>
                      <h4 className="text-white mb-1">Resultados da busca</h4>
                      <p className="text-white-50 mb-0">
                        Os eventos filtrados aparecem aqui e também são refletidos no calendário ao lado.
                      </p>
                    </div>
                    <span className="badge bg-brand text-white">{events.length}</span>
                  </div>

                  {loading ? (
                    <div className="empty-box">Carregando agenda pública...</div>
                  ) : events.length === 0 ? (
                    <div className="empty-box">
                      Nenhum evento encontrado com os filtros atuais. Experimente limpar a busca ou criar um
                      novo evento.
                    </div>
                  ) : (
                    <div className="row g-3">
                      {events.map((event) => (
                        <div className="col-12" key={event.id}>
                          <EventCard event={event} onClick={setSelectedEvent} showStatus={false} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-xl-5 d-grid gap-4">
              <div className="home-side-stack">
                <EventCalendar events={events} onSelectEvent={setSelectedEvent} />
              </div>

              <div className="card bg-onyx border-0 shadow-lg">
                <div className="card-body p-4">
                  <h5 className="text-white mb-2">Envie seu evento</h5>
                  <p className="text-white-50 mb-3">
                    Para sugerir um novo evento ou acompanhar pedidos, acesse o Portal do Solicitante.
                    Seu cadastro garante segurança e histórico das solicitações.
                  </p>
                  <Link to="/acesso" className="btn btn-brand w-100">
                    Acessar Portal do Solicitante
                  </Link>
                  <span className="text-white-50 small d-block mt-2">
                    Apenas usuários autenticados podem enviar ou acompanhar pedidos.
                  </span>
                </div>
              </div>

              <NewsletterPanel catalogs={catalogs} />
            </div>
          </div>

        </div>
      </section>

      {selectedEvent && (
        <div className="event-modal-backdrop" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal public-event-modal card bg-onyx border-0 shadow-lg" onClick={(event) => event.stopPropagation()}>
            <div className="card-body p-4">
              <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <p className="text-white-50 small mb-2">{selectedEvent.institution}</p>
                  <h5 className="text-white mb-1">{selectedEvent.title}</h5>
                  <p className="text-white-50 mb-0">{selectedEvent.promoter}</p>
                </div>
                <button type="button" className="btn btn-outline-light btn-sm" onClick={() => setSelectedEvent(null)}>
                  Fechar
                </button>
              </div>
              <div className="public-event-description mb-4">
                <EventDescription
                  description={selectedEvent.description}
                  format={selectedEvent.descriptionFormat}
                />
              </div>
              <EventDetailsGrid
                event={selectedEvent}
                onRegistrationClick={() => trackRegistrationClick(selectedEvent)}
              />
              {selectedEvent.registrationLink && (
                <a
                  className="btn btn-brand w-100 mt-3"
                  href={selectedEvent.registrationLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackRegistrationClick(selectedEvent)}
                >
                  Abrir inscrição
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
